import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { getApiUrl } from '../utils/apiUtils';
import { debugLog, debugError } from '../utils/debug';

const SubscriptionContext = createContext();

// Product configuration — must match App Store Connect product ID
const PRODUCT_ID = 'com.runium.app.weekly';
const WEEKLY_PRICE = '$3.99';
const TRIAL_DAYS = 7;

// How often to re-check subscription status (5 minutes)
const RECHECK_INTERVAL_MS = 5 * 60 * 1000;

// Default values when no provider is present (graceful fallback for admin pages etc.)
// isSubscribed defaults to false so unauthorized access is not granted if context fails
const defaultValue = {
  isSubscribed: false,
  isTrialing: false,
  isPromo: false,
  isLoading: false,
  error: null,
  subscriptionStatus: null,
  purchase: async () => ({ success: true }),
  startFreeTrial: async () => true,
  redeemPromoCode: async () => ({ success: false, error: 'Not available' }),
  restorePurchases: async () => ({ success: true }),
  checkSubscription: async () => {},
  PRODUCT_ID,
  WEEKLY_PRICE,
  TRIAL_DAYS,
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  return context || defaultValue;
};

/**
 * Initialize the cordova-plugin-purchase store for native IAP.
 * Returns the CdvPurchase.store instance, or null on web.
 */
const initializeStore = () => {
  try {
    if (!window.CdvPurchase) {
      debugLog('[IAP] CdvPurchase not available (web environment)');
      return null;
    }

    const { store, ProductType, Platform } = window.CdvPurchase;

    store.register([
      {
        id: PRODUCT_ID,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
      },
    ]);

    store.when()
      .productUpdated((product) => {
        debugLog('[IAP] Product updated:', product.id, product.pricing?.price);
      })
      .approved((transaction) => {
        debugLog('[IAP] Transaction approved:', transaction.transactionId);
        transaction.verify();
      })
      .verified((receipt) => {
        debugLog('[IAP] Receipt verified:', receipt.id);
        receipt.finish();
      })
      .finished((transaction) => {
        debugLog('[IAP] Transaction finished:', transaction.transactionId);
      });

    store.initialize([Platform.APPLE_APPSTORE]);

    debugLog('[IAP] Store initialized');
    return store;
  } catch (err) {
    debugError('[IAP] Store init failed:', err);
    return null;
  }
};

export const SubscriptionProvider = ({ children, getToken, userId }) => {
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => {
    if (userId) {
      const cached = localStorage.getItem(`subscription_status_${userId}`);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) {
            return parsed;
          }
        } catch (e) { /* ignore */ }
      }
    }
    return null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const storeRef = useRef(null);
  const recheckTimerRef = useRef(null);

  const isSubscribed = subscriptionStatus?.isActive === true;
  const isTrialing = subscriptionStatus?.isTrial === true;
  const isPromo = subscriptionStatus?.isPromo === true;

  // Helper to persist status
  const persistStatus = useCallback((status) => {
    setSubscriptionStatus(status);
    if (userId && status) {
      localStorage.setItem(`subscription_status_${userId}`, JSON.stringify(status));
    } else if (userId) {
      localStorage.removeItem(`subscription_status_${userId}`);
    }
  }, [userId]);

  // Check subscription status from backend
  const checkSubscription = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      const API_URL = getApiUrl();
      const token = getToken ? await getToken() : null;
      if (!token) { setIsLoading(false); return; }

      const response = await fetch(`${API_URL}/user/subscription-status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        const status = {
          isActive: data.is_active,
          isTrial: data.is_trial,
          isPromo: data.is_promo,
          expiresAt: data.expires_at,
          plan: data.plan,
          promoCode: data.promo_code,
        };
        persistStatus(status);
        debugLog('[Subscription] Status from server:', status);
      } else {
        // No subscription or error — check local cache expiry
        const cached = localStorage.getItem(`subscription_status_${userId}`);
        if (cached) {
          const parsed = JSON.parse(cached);
          if (!parsed.expiresAt || new Date(parsed.expiresAt) <= new Date()) {
            persistStatus(null);
          }
        }
      }
    } catch (err) {
      debugError('[Subscription] Failed to check status:', err);
      // Network error — trust local cache but check expiry
      if (subscriptionStatus?.expiresAt && new Date(subscriptionStatus.expiresAt) <= new Date()) {
        persistStatus(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, [userId, getToken, persistStatus, subscriptionStatus?.expiresAt]);

  // Start free trial via backend
  const startFreeTrial = useCallback(async () => {
    if (!userId) return false;

    try {
      const API_URL = getApiUrl();
      const token = getToken ? await getToken() : null;

      const response = await fetch(`${API_URL}/user/start-trial`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        persistStatus({
          isActive: true,
          isTrial: true,
          isPromo: false,
          expiresAt: data.expires_at,
          plan: 'weekly_trial',
        });
        debugLog('[Subscription] Trial started');
        return true;
      }
      return false;
    } catch (err) {
      debugError('[Subscription] Failed to start trial:', err);
      return false;
    }
  }, [userId, getToken, persistStatus]);

  // Redeem promo code — server-side only validation
  const redeemPromoCode = useCallback(async (code) => {
    if (!userId || !code) return { success: false, error: 'Invalid code' };

    const normalizedCode = code.trim().toUpperCase();

    try {
      const API_URL = getApiUrl();
      const token = getToken ? await getToken() : null;

      const response = await fetch(`${API_URL}/user/redeem-promo`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ code: normalizedCode }),
      });

      if (response.ok) {
        const data = await response.json();
        persistStatus({
          isActive: true,
          isTrial: false,
          isPromo: true,
          expiresAt: data.expires_at,
          plan: 'promo',
          promoCode: normalizedCode,
        });
        debugLog('[Subscription] Promo redeemed:', normalizedCode);
        return { success: true, freeDays: data.free_days };
      }

      const errorData = await response.json().catch(() => ({}));
      return { success: false, error: errorData.detail || 'Invalid promo code' };
    } catch (err) {
      debugError('[Subscription] Promo redeem failed:', err);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }, [userId, getToken, persistStatus]);

  // Validate Apple receipt with backend (declared before purchase to avoid TDZ)
  const validateReceipt = useCallback(async () => {
    try {
      const isNative = window.Capacitor?.isNativePlatform?.();
      if (!isNative || !storeRef.current) return;

      const product = storeRef.current.get(PRODUCT_ID);
      if (!product) return;

      // Get the latest transaction receipt
      const transaction = product.transactions?.[0];
      if (!transaction) return;

      const API_URL = getApiUrl();
      const token = getToken ? await getToken() : null;

      const response = await fetch(`${API_URL}/user/validate-receipt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          receipt_data: transaction.receipt || transaction.nativePurchase?.receipt,
          transaction_id: transaction.transactionId,
          product_id: PRODUCT_ID,
          platform: 'apple',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        persistStatus({
          isActive: data.is_active,
          isTrial: data.is_trial,
          isPromo: false,
          expiresAt: data.expires_at,
          plan: 'weekly',
        });
        debugLog('[IAP] Receipt validated:', data);
      }
    } catch (err) {
      debugError('[IAP] Receipt validation failed:', err);
    }
  }, [getToken, persistStatus]);

  // Helper: wait for the StoreKit product to be loaded, with timeout
  const waitForProduct = useCallback(async (timeoutMs = 12000) => {
    if (!storeRef.current) return null;

    // Try immediately first
    let product = storeRef.current.get(PRODUCT_ID);
    if (product && product.canPurchase) return product;

    // Force a refresh/update to fetch products from Apple
    try {
      if (typeof storeRef.current.update === 'function') {
        storeRef.current.update();
      }
    } catch (e) { /* ignore */ }

    // Poll for the product
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      await new Promise(r => setTimeout(r, 500));
      product = storeRef.current.get(PRODUCT_ID);
      if (product) {
        debugLog('[IAP] Product loaded:', PRODUCT_ID, 'canPurchase:', product.canPurchase);
        if (product.canPurchase) return product;
        // Product exists but can't purchase yet — still loading offers
      }
    }
    return product; // Return whatever we have, even if not ready
  }, []);

  // Send diagnostic data to backend for debugging
  const sendDiagnostic = useCallback(async (event, data) => {
    try {
      const API_URL = getApiUrl();
      const t = getToken ? await getToken() : null;
      if (!t) return;
      await fetch(`${API_URL}/iap/diagnostic`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ event, ...data, timestamp: new Date().toISOString() }),
      });
    } catch (e) { /* don't block on diagnostic */ }
  }, [getToken]);

  // Capture full StoreKit state for diagnostics
  const captureStoreState = useCallback(() => {
    const state = {
      hasCdvPurchase: !!window.CdvPurchase,
      hasCapacitor: !!window.Capacitor,
      isNative: !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()),
      storeRefExists: !!storeRef.current,
      cdvPurchaseKeys: window.CdvPurchase ? Object.keys(window.CdvPurchase).slice(0, 20) : [],
    };
    if (storeRef.current) {
      try {
        state.storeKeys = Object.keys(storeRef.current).slice(0, 30);
        state.allProducts = (storeRef.current.products || []).map(p => ({
          id: p.id,
          title: p.title,
          state: p.state,
          canPurchase: p.canPurchase,
          owned: p.owned,
          pricing: p.pricing,
          offers: (p.offers || []).map(o => ({ id: o.id, productId: o.productId })),
        }));
        state.targetProduct = null;
        const prod = storeRef.current.get(PRODUCT_ID);
        if (prod) {
          state.targetProduct = {
            id: prod.id,
            title: prod.title,
            state: prod.state,
            canPurchase: prod.canPurchase,
            pricing: prod.pricing,
            offerCount: (prod.offers || []).length,
          };
        }
        state.expectedProductId = PRODUCT_ID;
      } catch (e) {
        state.captureError = e.message;
      }
    }
    return state;
  }, []);

  // Purchase subscription — routes to native IAP or Stripe depending on platform
  const purchase = useCallback(async () => {
    try {
      const isNative = window.Capacitor?.isNativePlatform?.();

      if (isNative) {
        // --- iOS native IAP via StoreKit ---
        if (!window.CdvPurchase) {
          await sendDiagnostic('purchase_no_cdvpurchase', captureStoreState());
          return { success: false, error: 'In-app purchases are not available on this device. Please update the app to the latest version.' };
        }
        if (!storeRef.current) {
          await sendDiagnostic('purchase_no_store', captureStoreState());
          return { success: false, error: 'Store not initialized. Please restart the app and try again.' };
        }

        debugLog('[IAP] Starting native purchase for', PRODUCT_ID);

        // Send pre-purchase diagnostic
        await sendDiagnostic('purchase_started', captureStoreState());

        // Wait for product to be loaded by Apple's servers
        const product = await waitForProduct(12000);

        // Send post-wait diagnostic
        await sendDiagnostic('purchase_after_wait', { ...captureStoreState(), productFound: !!product });

        if (!product) {
          return {
            success: false,
            error: 'Subscription not available. This usually means you need to sign into a Sandbox tester account: Settings → App Store → Sandbox Account. If using your real Apple ID, please use a Sandbox tester from App Store Connect.',
          };
        }

        const offer = product.getOffer ? product.getOffer() : (product.offers && product.offers[0]);
        if (!offer) {
          await sendDiagnostic('purchase_no_offer', captureStoreState());
          return {
            success: false,
            error: 'Subscription offer is still loading. Please wait a moment and try again.',
          };
        }

        // This triggers the native Apple IAP dialog (payment info collected by Apple)
        const result = await storeRef.current.order(offer);

        if (result && result.isError) {
          if (result.code === 'E_USER_CANCELLED') {
            return { success: false, error: 'Purchase cancelled' };
          }
          return { success: false, error: result.message || 'Purchase failed' };
        }

        // Purchase succeeded — validate receipt on server
        await validateReceipt();
        return { success: true };
      }

      // --- Web: Stripe Checkout ---
      debugLog('[Subscription] Web purchase flow - redirecting to Stripe');
      const API_URL = getApiUrl();
      const token = getToken ? await getToken() : null;

      const response = await fetch(`${API_URL}/user/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          success_url: window.location.origin + '/?subscription=success',
          cancel_url: window.location.origin + '/?subscription=cancelled',
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        return { success: false, error: err.detail || 'Could not start checkout. Please try again.' };
      }

      const data = await response.json();
      if (data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
        // Return pending — page will redirect
        return { success: true, pending: true };
      }

      return { success: false, error: 'Checkout unavailable. Please try again.' };
    } catch (err) {
      debugError('[Subscription] Purchase failed:', err);
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [getToken, validateReceipt, waitForProduct, sendDiagnostic, captureStoreState]);

  // Restore purchases — native IAP + backend check
  const restorePurchases = useCallback(async () => {
    try {
      const isNative = window.Capacitor?.isNativePlatform?.();

      if (isNative && storeRef.current) {
        debugLog('[IAP] Restoring purchases...');
        await storeRef.current.restorePurchases();
        // After restore, validate with backend
        await validateReceipt();
      }

      // Always also check backend
      await checkSubscription();
      return { success: true };
    } catch (err) {
      debugError('[Subscription] Restore failed:', err);
      return { success: false, error: err.message };
    }
  }, [checkSubscription, validateReceipt]);

  // Initialize store on native platforms — poll for CdvPurchase availability
  useEffect(() => {
    const isNative = window.Capacitor?.isNativePlatform?.();
    if (!isNative || storeRef.current) return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 20; // 10 seconds total

    const tryInit = () => {
      if (cancelled || storeRef.current) return;
      attempts += 1;
      if (window.CdvPurchase) {
        debugLog(`[IAP] CdvPurchase available after ${attempts} attempts`);
        storeRef.current = initializeStore();
      } else if (attempts < maxAttempts) {
        setTimeout(tryInit, 500);
      } else {
        debugError('[IAP] CdvPurchase never became available after 10 seconds');
      }
    };

    tryInit();

    return () => { cancelled = true; };
  }, []);

  // Check subscription on mount
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Periodic re-check to catch expiry
  useEffect(() => {
    recheckTimerRef.current = setInterval(() => {
      // Quick local expiry check
      if (subscriptionStatus?.expiresAt) {
        const expiresAt = new Date(subscriptionStatus.expiresAt);
        if (expiresAt <= new Date()) {
          debugLog('[Subscription] Subscription expired, re-checking...');
          persistStatus(null);
          checkSubscription();
        }
      }
    }, RECHECK_INTERVAL_MS);

    return () => {
      if (recheckTimerRef.current) {
        clearInterval(recheckTimerRef.current);
      }
    };
  }, [subscriptionStatus?.expiresAt, checkSubscription, persistStatus]);

  // Also re-check when app comes back to foreground
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check local expiry immediately
        if (subscriptionStatus?.expiresAt && new Date(subscriptionStatus.expiresAt) <= new Date()) {
          persistStatus(null);
        }
        checkSubscription();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [checkSubscription, subscriptionStatus?.expiresAt, persistStatus]);

  const value = {
    isSubscribed,
    isTrialing,
    isPromo,
    isLoading,
    error,
    subscriptionStatus,
    purchase,
    startFreeTrial,
    redeemPromoCode,
    restorePurchases,
    checkSubscription,
    PRODUCT_ID,
    WEEKLY_PRICE,
    TRIAL_DAYS,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};
